import React, { useMemo, useCallback } from "react"
import { useFetcher } from "react-router"
import { LoaderCircleIcon, SaveIcon, PlusIcon, PencilIcon, TrashIcon, XCircleIcon } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

import type { EnhancedConfigData } from "./brand-lenses-configuration"

interface FetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}
interface SubmitPayload {
  action: "SAVE_SECTION" | "REMOVE_SECTION"
  organisation: string
  project: string
  jsonPath: string
  concurrency: string
  value?: string[]
}

export function DialogueRolesTab({ data, config }: { data: EnhancedConfigData; config: string[] | null | undefined }) {
  const fetcher = useFetcher<FetcherData>()

  const initialRoles = useMemo(() => {
    if (config == null || !Array.isArray(config) || config.length === 0) {
      return []
    }
    return config
  }, [config])

  const [roles, setRoles] = React.useState<string[]>(initialRoles)
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null)
  const [editValue, setEditValue] = React.useState("")
  const [newRoleName, setNewRoleName] = React.useState("")
  const pendingAction = (fetcher.json as SubmitPayload | undefined)?.action ?? null

  React.useEffect(() => {
    setRoles(initialRoles)
  }, [initialRoles])

  const handleAddRole = useCallback(() => {
    const roleName = newRoleName.trim()

    if (roleName !== "" && !roles.includes(roleName)) {
      setRoles([...roles, roleName])
      setNewRoleName("")
    }
  }, [newRoleName, roles])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleAddRole()
      }
    },
    [handleAddRole],
  )

  const startEdit = useCallback(
    (index: number) => {
      setEditingIndex(index)
      setEditValue(roles[index])
    },
    [roles],
  )

  const saveEdit = useCallback(() => {
    if (editingIndex !== null && editValue.trim().length > 0) {
      const updatedRoles = [...roles]
      updatedRoles[editingIndex] = editValue.trim()
      setRoles(updatedRoles)
      setEditingIndex(null)
      setEditValue("")
    }
  }, [editingIndex, editValue, roles])

  const cancelEdit = useCallback(() => {
    setEditingIndex(null)
    setEditValue("")
  }, [])

  const removeRole = useCallback((index: number) => setRoles((r) => r.filter((_, i) => i !== index)), [])

  const handleRemoveSection = useCallback(() => {
    fetcher.submit(
      {
        action: "REMOVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.availableDialogueRoles",
        concurrency: data.existingConfig?.concurrency ?? "",
      },
      { method: "post", encType: "application/json" },
    )
  }, [fetcher, data])

  const handleSave = useCallback(
    (formEvent: React.FormEvent<HTMLFormElement>) => {
      formEvent.preventDefault()

      const submitData = {
        action: "SAVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.availableDialogueRoles",
        value: roles,
        concurrency: data.existingConfig?.concurrency ?? "",
      }

      fetcher.submit(submitData, {
        method: "post",
        encType: "application/json",
      })
    },
    [fetcher, data, roles, handleRemoveSection],
  )

  return (
    <div className="flex h-full flex-col">
      <form onSubmit={handleSave} className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-6">
            <div className="rounded-lg border p-4">
              <h3 className="mb-4 text-lg font-medium">Add New Role</h3>
              <div className="flex gap-3">
                <Input
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter role name..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddRole}
                  disabled={newRoleName.trim().length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <h3 className="mb-4 text-lg font-medium">Configured Roles ({roles.length})</h3>

              {roles.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-border py-12 text-center text-muted-foreground">
                  <p className="text-sm">No dialogue roles configured</p>
                  <p className="mt-1 text-xs">Add your first role above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {roles.map((role, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[1fr_auto] items-center gap-4 rounded-lg border bg-muted p-3"
                    >
                      <div className="min-w-0">
                        {editingIndex === index ? (
                          <div className="flex gap-2">
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault()
                                  saveEdit()
                                } else if (e.key === "Escape") {
                                  cancelEdit()
                                }
                              }}
                              className="flex-1"
                              autoFocus
                            />
                            <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}>
                              Cancel
                            </Button>
                            <Button type="button" size="sm" onClick={saveEdit}>
                              Save
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-xs text-muted-foreground">#{index + 1}</span>
                            <span className="truncate font-medium">{role}</span>
                          </div>
                        )}
                      </div>

                      {editingIndex !== index && (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(index)}
                            className="text-info hover:bg-info/10"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent side="left" className="w-80">
                              <p className="text-sm">Delete role &quot;{role}&quot;?</p>
                              <div className="flex items-center justify-end gap-3 pt-4">
                                <PopoverClose asChild>
                                  <Button type="button" variant="ghost" size="sm">
                                    Cancel
                                  </Button>
                                </PopoverClose>
                                <PopoverClose asChild>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => removeRole(index)}
                                  >
                                    Delete
                                  </Button>
                                </PopoverClose>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-t p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button type="submit" disabled={fetcher.state !== "idle"} className="flex-1 bg-info hover:bg-info/90">
              {pendingAction === "SAVE_SECTION" && fetcher.state !== "idle" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Available Dialogue Roles
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={fetcher.state !== "idle"}
                >
                  {pendingAction === "REMOVE_SECTION" && fetcher.state !== "idle" ? (
                    <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircleIcon className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Available Dialogue Roles?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all dialogue role configurations and reset this tab to its default state.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button type="button" variant="destructive" size="sm" onClick={() => void handleRemoveSection()}>
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </form>
    </div>
  )
}
